import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Bold, 
  Italic, 
  Underline,
  AlignLeft,
  AlignCenter, 
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Indent,
  Outdent,
  RotateCcw,
  RotateCw,
  Eraser
} from 'lucide-react';
import { useDocumentFormat } from '@/hooks/useDocumentFormat';
import { saveSelection, preventFocusLoss } from '@/utils/selectionManager';

interface DocumentToolbarNewProps {
  contentEditableRef: React.RefObject<HTMLDivElement>;
  saveSnapshot?: (content: string, force?: boolean) => void;
  undo?: () => void;
  redo?: () => void;
  canUndo?: () => boolean;
  canRedo?: () => boolean;
}

const DocumentToolbarNew = ({ 
  contentEditableRef, 
  saveSnapshot,
  undo,
  redo,
  canUndo,
  canRedo
}: DocumentToolbarNewProps) => {
  const [selectionFormat, setSelectionFormat] = useState<any>({});

  // Hook de formatação com sistema completo
  const {
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setTextAlign,
    setFontFamily,
    insertUnorderedList,
    insertOrderedList,
    indent,
    outdent,
    removeFormat,
    getSelectionFormat
  } = useDocumentFormat({
    contentEditableRef,
    saveSnapshot: saveSnapshot ? () => saveSnapshot(contentEditableRef.current?.innerHTML || '', false) : undefined
  });

  // Salvar seleção para preservar durante interações
  const handleSaveSelection = useCallback(() => {
    if (contentEditableRef.current) {
      saveSelection(contentEditableRef.current);
    }
  }, [contentEditableRef]);

  // Atualizar estado da formatação quando seleção muda
  const updateSelectionFormat = useCallback(() => {
    try {
      setSelectionFormat(getSelectionFormat());
    } catch (error) {
      console.warn('Erro ao verificar estado da formatação:', error);
    }
  }, [getSelectionFormat]);

  // Listeners para atualizar formatação
  useEffect(() => {
    const element = contentEditableRef.current;
    if (element) {
      element.addEventListener('mouseup', updateSelectionFormat);
      element.addEventListener('keyup', updateSelectionFormat);
      element.addEventListener('focus', updateSelectionFormat);
      
      return () => {
        element.removeEventListener('mouseup', updateSelectionFormat);
        element.removeEventListener('keyup', updateSelectionFormat);
        element.removeEventListener('focus', updateSelectionFormat);
      };
    }
  }, [contentEditableRef, updateSelectionFormat]);

  return (
    <div className="flex items-center justify-center gap-1 bg-white p-2">
      {/* Formatação de Texto */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={selectionFormat.bold ? "default" : "outline"}
              size="sm"
              onClick={toggleBold}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Bold className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Negrito (Ctrl+B)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={selectionFormat.italic ? "default" : "outline"}
              size="sm"
              onClick={toggleItalic}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Italic className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Itálico (Ctrl+I)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={selectionFormat.underline ? "default" : "outline"}
              size="sm"
              onClick={toggleUnderline}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Underline className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sublinhado (Ctrl+U)</TooltipContent>
        </Tooltip>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Família da Fonte */}
      <Select onValueChange={setFontFamily} onOpenChange={(open) => { if (open) handleSaveSelection(); }}>
        <SelectTrigger className="w-32 h-8" onMouseDown={preventFocusLoss}>
          <SelectValue placeholder="Fonte" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Times New Roman">Times New Roman</SelectItem>
          <SelectItem value="Arial">Arial</SelectItem>
          <SelectItem value="Georgia">Georgia</SelectItem>
          <SelectItem value="Verdana">Verdana</SelectItem>
          <SelectItem value="Courier New">Courier New</SelectItem>
        </SelectContent>
      </Select>

      <Separator orientation="vertical" className="h-6" />

      {/* Alinhamento */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTextAlign('left')}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <AlignLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Alinhar à Esquerda</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTextAlign('center')}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <AlignCenter className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Centralizar</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTextAlign('right')}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <AlignRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Alinhar à Direita</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTextAlign('justify')}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <AlignJustify className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Justificar</TooltipContent>
        </Tooltip>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Listas e Indentação */}
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={insertUnorderedList}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <List className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Lista com Marcadores</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={insertOrderedList}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Lista Numerada</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={outdent}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Outdent className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Diminuir Recuo</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={indent}
              className="w-8 h-8 p-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Indent className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Aumentar Recuo</TooltipContent>
        </Tooltip>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Undo/Redo */}
      {(undo || redo) && (
        <>
          <div className="flex items-center gap-1">
            {undo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={undo}
                    disabled={canUndo ? !canUndo() : false}
                    className="w-8 h-8 p-0"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Desfazer (Ctrl+Z)</TooltipContent>
              </Tooltip>
            )}

            {redo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={redo}
                    disabled={canRedo ? !canRedo() : false}
                    className="w-8 h-8 p-0"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Refazer (Ctrl+Y)</TooltipContent>
              </Tooltip>
            )}
          </div>
          <Separator orientation="vertical" className="h-6" />
        </>
      )}

      {/* Remover Formatação */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={removeFormat}
            className="w-8 h-8 p-0"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Eraser className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Remover Formatação</TooltipContent>
      </Tooltip>
    </div>
  );
};

export default DocumentToolbarNew;